/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Phase 3.6 R2 + Phase A R4 A2 — DJ Board pop-out.
 *
 * This window is the PRIMARY DJ surface as of R4. The main tab shows a
 * compact <DjLivePanel> with an "Open DJ Board" button; clicking it
 * opens this window which renders the full DjConsole2 in pop-out mode.
 * The engine + LiveKit publish stay in the main tab — closing this
 * window does NOT stop audio.
 *
 * Phase A R4 A2 — close-window warning: if the DJ tries to close this
 * window while audio is playing or a recording is in progress, show the
 * browser-native confirm dialog explaining the consequences. The engine
 * continues either way, but we don't want them losing the controls by
 * accident.
 *
 * Phase A R8.2 — install global error surfacing for the popout. Any
 * `window.error` / `unhandledrejection` / `sr-toast` event becomes a
 * visible red toast so future silent failures (the kind that hid 3
 * rounds of bugs) immediately become user-visible.
 */
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import DjConsole2 from "../components/DjConsole2";
import "../styles/dj-skins.css";

export default function DjBoardPopout() {
  const { roomId } = useParams();

  // Phase A R5 B3 — beforeunload. The popout window IS the live DJ
  // surface, so any close attempt while a roomId is present should
  // confirm with the user. Modern browsers ignore custom strings but
  // honor preventDefault + returnValue assignment.
  useEffect(() => {
    if (!roomId) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [roomId]);

  // Phase A R8.2 — global error surfacing for the popout. Any uncaught
  // exception or unhandled promise rejection is logged and surfaced as
  // a red toast. `sr-toast` custom events dispatched by deep code paths
  // (engine, AutoDjPanel, DjConsole2) are also bridged to sonner toasts
  // so the user actually sees them. Without this, the silent-catch
  // failure modes in earlier rounds were invisible to the user.
  useEffect(() => {
    const onError = (e) => {
      // R14.2-A — preventDefault + zero returnValue so CRA's dev error
      // overlay never takes over. Surface as red toast.
      try { e.preventDefault(); } catch { /* */ }
      try { e.returnValue = ""; } catch { /* */ }
      // eslint-disable-next-line no-console
      console.error("[UNCAUGHT]", e.message, e.error);
      try { toast.error(`Internal error — ${e.message || "open console for details"}`); } catch { /* */ }
    };
    const onRejection = (e) => {
      try { e.preventDefault(); } catch { /* */ }
      try { e.returnValue = ""; } catch { /* */ }
      // eslint-disable-next-line no-console
      console.error("[UNCAUGHT promise]", e.reason);
      try { toast.error(`Internal error (promise) — ${e.reason?.message || e.reason || "open console for details"}`); } catch { /* */ }
    };
    const onSrToast = (e) => {
      const { type = "info", msg = "" } = e.detail || {};
      try {
        if (type === "error")        toast.error(msg);
        else if (type === "success") toast.success(msg);
        else if (type === "warning") toast.warning(msg);
        else                          toast.info(msg);
      } catch { /* */ }
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("sr-toast", onSrToast);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("sr-toast", onSrToast);
    };
  }, []);

  if (!roomId) return null;
  return (
    <div className="min-h-screen bg-[var(--sr-bg,#0a0a0e)] text-white" data-testid="dj-board-popout-root">
      <DjConsole2
        mode="popout"
        remoteRoomId={roomId}
        room={null}
        layout="inline"
      />
    </div>
  );
}
